<script setup lang="ts">
import Button from '../design-system/button.vue';
import Drawer from '~/components/design-system/drawer.vue';
import ToggleSwitch from 'primevue/toggleswitch';
import { ArrowUturnLeftIcon } from '@heroicons/vue/24/solid';
import type { FeatureFlag } from '~/interfaces/dto/config/response';
import { useDebounceFn } from '@vueuse/core';

const { $enabled } = useNuxtApp();

const active = ref<boolean>(false);
const siteStore = useSiteStore();

const originalFeatureFlags = ref();
const featureFlags = ref<FeatureFlag[]>(siteStore.getConfig?.featureFlags);
const searchText = ref<string>();
const catToggle = ref();

const emits = defineEmits<{
  toggleFeatureFlag: [
    args: {
      flagIdentifier?: string;
      group?: string;
      setting?: string;
      val?: boolean;
      rehydrating?: boolean;
    },
  ];
  resetFeatureFlags: [];
}>();

const featureFlagIdentifier = computed(
  () =>
    ({ group, setting }) =>
      `${group}.${setting}`.toLowerCase(),
);

function sortFeatureFlags(ff: FeatureFlag[]) {
  const sortedFF: FeatureFlag[] = ff;
  sortedFF.forEach((featureGroup) => {
    featureGroup.features.sort((a, b) => a.key.localeCompare(b.key));
  });

  featureFlags.value = sortedFF;
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

function searchFF() {
  featureFlags.value = siteStore.getConfig?.featureFlags.map((ff) => {
    const filteredFeat = ff.features.filter((feat) =>
      feat.key.toLowerCase().includes(searchText.value.toLowerCase()),
    );
    return { ...ff, features: filteredFeat };
  });
}

function setFFCategory(cat: string, index: number) {
  catToggle.value[cat] = !catToggle.value[cat];
  featureFlags.value[index].features.forEach((feat) => {
    emits('toggleFeatureFlag', {
      group: cat,
      setting: feat.key,
      val: catToggle.value[cat],
    });
  });
}

function reset() {
  searchText.value = '';
  emits('resetFeatureFlags');
  // const newConfig = {
  //   ...siteStore.getConfig,
  //   featureFlags: originalFeatureFlags.value,
  // };
  // siteStore.setConfig(newConfig);
  // featureFlags.value = siteStore.getConfig?.featureFlags;
  // Object.keys(catToggle.value).forEach((cat) => (catToggle.value[cat] = false));
}

const debounceSearch = useDebounceFn(() => searchFF(), 350);
</script>
<template>
  <div>
    <Drawer
      v-model:visible="active"
      position="bottom"
      custom-container-class="bg-base-secondary p-4 overflow-y-auto border-none"
    >
      <template #activator>
        <div class="flex gap-2">
          <Button
            class="font-normal w-full justify-center"
            type="primary"
            padding="md"
            rounding="md"
            @click="active = true"
          >
            Toggle feature flags
          </Button>
          <Button
            class="font-normal std-border"
            type="secondary"
            padding="md"
            rounding="md"
            @click="reset()"
          >
            <ArrowUturnLeftIcon />
          </Button>
        </div>
        <div class="text-base-priority text-xs mt-1">
          Automatic persistence applied, settings stay until reset or session
          end
        </div>
      </template>
      <template #header>
        <div class="flex-center flex-wrap gap-3 mb-2">
          <p class="text-base-priority text-lg font-bold mb-1">Feature Flags</p>

          <Button
            type="secondary-alternative"
            padding="wide-short"
            rounding="md"
            @click="sortFeatureFlags(featureFlags)"
          >
            Sort
          </Button>
          <label for="search" class="text-base-priority ml-3">Search</label>
          <input
            v-model="searchText"
            name="search"
            class="pl-3 p-2 text-md text-base-priority bg-layer h-min rounded-md stacked-border"
            @update:model-value="debounceSearch()"
          />
          <Button
            type="secondary-alternative"
            padding="wide-short"
            rounding="md"
            class="ml-3"
            @click="reset()"
          >
            Reset
          </Button>
        </div>
      </template>
      <div
        class="text-base-priority grid grid-cols-1 md:grid-cols-4 gap-2 max-h-[60vh] overflow-scroll"
      >
        <div
          v-for="({ name, key, features }, index) in featureFlags"
          :key="`ff-${index}`"
          class="bg-layer p-2 rounded-lg"
        >
          <div class="flex content-center flex-nowrap">
            <div class="font-bold">{{ name }}</div>
            <ToggleSwitch
              :key="name"
              :model-value="catToggle?.[key]"
              class="ml-2 mb-2"
              @update:model-value="setFFCategory(key, index)"
            />
          </div>
          <hr class="mb-1 stacked-border-color" />
          <div
            v-for="({ name: ffName, key: ffKey }, fIndex) in features"
            :key="`ff-${fIndex}-${index}`"
            class="flex justify-between items-center mb-1 w-full"
          >
            <div class="w-9/12">
              <div class="truncate cursor-help" :title="ffName">
                {{ ffName }}
              </div>
              <div
                class="text-xs cursor-copy truncate"
                @click="
                  copyToClipboard(
                    featureFlagIdentifier({ group: key, setting: ffKey }),
                  )
                "
              >
                {{ featureFlagIdentifier({ group: key, setting: ffKey }) }}
              </div>
            </div>

            <ToggleSwitch
              :model-value="
                $enabled(featureFlagIdentifier({ group: key, setting: ffKey }))
              "
              class="h-fit"
              @change="
                emits('toggleFeatureFlag', { group: key, setting: ffKey })
              "
            />
          </div>
        </div>
      </div>
    </Drawer>
  </div>
</template>
